// External imports
import React from 'react'
import { render } from 'react-dom'
import { compose, applyMiddleware, createStore } from 'redux';
import { Provider } from 'react-redux'
import { map, filter } from 'ramda';
import thunk from 'redux-thunk'
import { composeWithDevTools } from 'redux-devtools-extension'
import { pdfjs } from 'react-pdf';
import axios from 'axios';
import "typeface-noto-sans";
import "typeface-noto-serif";
import 'typeface-hind';
import 'typeface-sintony';
import 'typeface-karla';
import 'typeface-spectral';
import 'typeface-lato';
import 'typeface-heebo';

import 'bootstrap/dist/css/bootstrap.css';
import { library } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';

import { reduxSearch, SearchApi, INDEX_MODES } from 'redux-search';
import createDebounce from 'redux-debounced';

import flashMiddleware from './middlewares/flashMiddleware';
import logMiddleware from './middlewares/logMiddleware';
import apiMiddleware from './middlewares/apiMiddleware';
import processQueueMiddleware from './middlewares/processQueueMiddleware';

// Local imports
import App from './App'
import rootReducer from './reducers'
import { horsesList, salesFind, updatesList } from './actions/apiActions';
import { refreshIfSaleDay } from "./actions/utilActions";

// Assets
import './index.scss'

// all-substrings match by default; same as current
// eg "c", "ca", "a", "at", "cat" match "cat"
const allSubstringsSearchApi = new SearchApi()

// exact words matching (eg only "cat" matches "cat")
const exactWordsSearchApi = new SearchApi({
  indexMode: INDEX_MODES.EXACT_WORDS
})

library.add(fas, far, fab);

const enhancer = compose(
  applyMiddleware(
      createDebounce(),
      logMiddleware,
      processQueueMiddleware,
      apiMiddleware,
      flashMiddleware,
  ),
  reduxSearch({
      // Configure redux-search by telling it which resources to index for searching
      resourceIndexes: {
          horses: ['name', 'dam', 'sire', 'consignor', 'consignor_name', 'foaled', 'purchaser', 'covering_sire'],
          sire: ['sire', ],
          consignor_name: ['consignor', 'consignor_name', ],
          dam: ['dam', ],
          purchaser: ['purchaser', ],
          covering_sire: ['covering_sire', ],
          foaled: ['foaled', ],
      },
      resourceSelector: (resourceName, state) => {
          return state.horses;
      },
      searchApi: allSubstringsSearchApi,
  }),
);

const store = createStore(
  rootReducer,
  {},
  enhancer,
)

function observeStore(store, select, onChange) {
  let currentState;

  function handleChange() {
    const nextState = select(store.getState());
    if (typeof currentState === 'undefined') {
      currentState = null;
      return;
    }
    if (nextState !== currentState) {
      currentState = nextState;
      onChange(currentState);
    }
  }

  const unsubscribe = store.subscribe(handleChange);
  handleChange();
  return unsubscribe;
}

observeStore(store, ({ horses }) => horses, (horses) => {
  if(horses.length === 0) {
    return;
  }

  const favoritedHorses = filter(h => h.favorited)(horses);
  localStorage.setItem('horses_K26H', JSON.stringify(map(h => h.hip)(favoritedHorses)));
});

async function setupApp() {
  const drupalSettingsRequest = await axios({
    url: 'https://www.fasigtipton.com/ec/K26H/',
    method: 'get',
    responseType: 'json',
  });

  const consignorsRequest = await axios({
    url: 'https://www.fasigtipton.com/ft-api/consignors',
    method: 'get',
    responseType: 'json',
    headers: {
      'Cache-Control': 'no-cache',
    }
  });
  
  global.drupalSettings = {
    catalogues: drupalSettingsRequest.data,
  }

  global.drupalSettings.catalogues.consignors_contacts = consignorsRequest.data;
  global.drupalSettings.catalogues.sale_name = global.drupalSettings.catalogues.sale_name + ' 2026'

  if(!global.drupalSettings.catalogues.ec_is_published && !global.drupalSettings.catalogues.is_admin && !global.drupalSettings.catalogues.can_admin_updates) {
    return false;
  }
  
  store.dispatch(salesFind(global.drupalSettings.catalogues.sale_identifier))
      .then(() => {
          store.dispatch(horsesList());
          store.dispatch(updatesList());
          refreshIfSaleDay(store, global.drupalSettings.catalogues.sale_start_date, global.drupalSettings.catalogues.sale_end_date);
      });
  
  render(
    <Provider store={store}>
      <App />
    </Provider>,
    document.getElementById('root')
  )
  
}
setupApp();
